import { useMemo, useState } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { ChatbotChartHint } from "../../services/chatbot.service";
import { ParsedTable, toNumber } from "./markdownTable";

type ChartType = "bar" | "line" | "pie";

interface ChatbotChartProps {
  table: ParsedTable;
  hint?: ChatbotChartHint | null;
}

const PALETTE = ["#0b5cab", "#12b76a", "#f79009", "#7a5af8", "#ef4444", "#06aed4", "#ec4899"];

const looksTemporal = (label: string): boolean =>
  /\b(20\d{2})\b/.test(label) || /jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec/i.test(label) || /^\d{4}-\d{2}/.test(label);

/** Renders an inline chart for a tabular chatbot answer. Returns null when the table has no plottable numeric series. */
const ChatbotChart = ({ table, hint }: ChatbotChartProps): JSX.Element | null => {
  const model = useMemo(() => {
    const { columns, rows } = table;
    if (columns.length < 2 || rows.length === 0) return null;

    // Which columns are numeric (majority of cells parse as numbers)?
    const numericByCol = columns.map((_, ci) => {
      const vals = rows.map((r) => toNumber(r[ci]));
      const numeric = vals.filter((v) => !Number.isNaN(v)).length;
      return numeric >= Math.ceil(rows.length / 2) && numeric > 0;
    });

    const validHintKey = (k?: string | null) => (k && columns.includes(k) ? k : null);

    // x-axis: hint, else first non-numeric column, else first column.
    const xKey =
      validHintKey(hint?.xKey) ??
      columns.find((_, ci) => !numericByCol[ci]) ??
      columns[0];
    const xIndex = columns.indexOf(xKey);

    // series: hint (numeric only), else all numeric columns except the x column.
    let seriesKeys = (hint?.seriesKeys ?? [])
      .filter((k) => columns.includes(k) && numericByCol[columns.indexOf(k)]);
    if (seriesKeys.length === 0) {
      seriesKeys = columns.filter((c, ci) => numericByCol[ci] && ci !== xIndex);
    }
    if (seriesKeys.length === 0) return null;

    const data = rows.map((r) => {
      const obj: Record<string, string | number> = { [xKey]: r[xIndex] ?? "" };
      seriesKeys.forEach((k) => {
        const v = toNumber(r[columns.indexOf(k)]);
        obj[k] = Number.isNaN(v) ? 0 : v;
      });
      return obj;
    });

    const allTemporal = data.every((d) => looksTemporal(String(d[xKey])));
    const defaultType: ChartType =
      hint?.chartType === "bar" || hint?.chartType === "line" || hint?.chartType === "pie"
        ? (hint.chartType as ChartType)
        : seriesKeys.length === 1 && rows.length <= 6 && !allTemporal
        ? "pie"
        : allTemporal
        ? "line"
        : "bar";

    return { xKey, seriesKeys, data, defaultType };
  }, [table, hint]);

  const [type, setType] = useState<ChartType>(model?.defaultType ?? "bar");

  if (!model) return null;
  const { xKey, seriesKeys, data } = model;

  return (
    <div className="chatbot-chart">
      <div className="chatbot-chart-toolbar">
        {hint?.title && <span className="chatbot-chart-title sub_text">{hint.title}</span>}
        <div className="chatbot-chart-types">
          {(["bar", "line", "pie"] as ChartType[]).map((t) => (
            <button
              key={t}
              className={`chatbot-chart-type${type === t ? " active" : ""}`}
              onClick={() => setType(t)}
              aria-pressed={type === t}
            >
              {t}
            </button>
          ))}
        </div>
      </div>

      <ResponsiveContainer width="100%" height={220}>
        {type === "line" ? (
          <LineChart data={data} margin={{ top: 8, right: 12, bottom: 4, left: 4 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--Grey-Divider)" />
            <XAxis dataKey={xKey} tick={{ fontSize: 10 }} />
            <YAxis tick={{ fontSize: 10 }} width={44} />
            <Tooltip />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            {seriesKeys.map((k, i) => (
              <Line key={k} type="monotone" dataKey={k} stroke={PALETTE[i % PALETTE.length]} strokeWidth={2} dot={false} />
            ))}
          </LineChart>
        ) : type === "pie" ? (
          <PieChart>
            <Tooltip />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <Pie data={data} dataKey={seriesKeys[0]} nameKey={xKey} outerRadius={80} label={{ fontSize: 10 }}>
              {data.map((_, i) => (
                <Cell key={i} fill={PALETTE[i % PALETTE.length]} />
              ))}
            </Pie>
          </PieChart>
        ) : (
          <BarChart data={data} margin={{ top: 8, right: 12, bottom: 4, left: 4 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--Grey-Divider)" />
            <XAxis dataKey={xKey} tick={{ fontSize: 10 }} />
            <YAxis tick={{ fontSize: 10 }} width={44} />
            <Tooltip />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            {seriesKeys.map((k, i) => (
              <Bar key={k} dataKey={k} fill={PALETTE[i % PALETTE.length]} />
            ))}
          </BarChart>
        )}
      </ResponsiveContainer>
    </div>
  );
};

export default ChatbotChart;
